import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { api } from '../services/api';
import { ServiceOfferingResponse } from '../types';
import { useLanguage } from '../i18n/LanguageContext';
import AddServiceModalV2 from '../components/services/AddServiceModalV2';
import DeleteServiceModal from '../components/services/DeleteServiceModal';
import { useAdminActionFeedContext } from '../context/AdminActionFeedContext';

function ServiceDetailPage() {
  const { serviceId } = useParams<{ serviceId: string }>();
  const auth = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { unreadOfferingIds, clearOffering } = useAdminActionFeedContext();
  const hasUnread = !!serviceId && unreadOfferingIds.has(serviceId);

  const [service, setService] = useState<ServiceOfferingResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const fetchService = async () => {
    if (!auth.token || !serviceId) return;
    try {
      const services = await api.getMyServices(auth.token);
      const found = services.find((s) => s.id === serviceId);
      if (found) {
        setService(found);
      } else {
        setError('Service not found');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load service');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchService();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.token, serviceId]);

  const formatDuration = (minutes: number) => {
    const days = Math.floor(minutes / (24 * 60));
    const hours = Math.floor((minutes % (24 * 60)) / 60);
    if (days > 0 && hours > 0) return `${days} days ${hours} hours`;
    if (days > 0) return `${days} days`;
    if (hours > 0) return `${hours} hours`;
    return '0 hours';
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return <span className="partner-status-badge active">{t('services.status.active')}</span>;
      case 'INACTIVE':
        return <span className="partner-status-badge inactive">{t('services.status.inactive')}</span>;
      case 'PENDING_APPROVAL':
        return <span className="partner-status-badge pending">{t('services.status.pending')}</span>;
      default:
        return <span className="partner-status-badge">{status}</span>;
    }
  };

  if (loading) {
    return (
      <div className="partner-service-detail">
        <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem' }}>{t('common.loading')}</p>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="partner-service-detail">
        <div className="error-message">{error || 'Service not found'}</div>
        <button onClick={() => navigate('/services')} style={{ marginTop: '1rem', color: '#4f46e5', background: 'none', border: 'none', cursor: 'pointer' }}>
          &larr; {t('services.backToServices')}
        </button>
      </div>
    );
  }

  const serviceName = service.name || service.category.name;
  const categoryName = service.category.parentId
    ? (service.category.parentName || '-')
    : service.category.name;
  const subcategoryName = service.category.parentId ? service.category.name : '-';
  const thumbnails = service.media?.filter((m) => m.mediaType === 'SERVICE_THUMBNAIL') || [];
  const photos = service.media?.filter((m) => m.mediaType === 'SERVICE_PHOTO' || m.mediaType === 'SERVICE_IMAGE') || [];

  return (
    <div className="partner-service-detail">
      {/* Breadcrumb */}
      <div className="partner-service-breadcrumb">
        <Link to="/services">{t('services.title')}</Link>
        {' > '}
        <span>{serviceName}</span>
      </div>

      {/* Header */}
      <div className="partner-service-detail-header">
        <div className="partner-service-detail-header-left">
          <h1>
            {serviceName}
            {hasUnread && serviceId && (
              <span
                className="partner-services-update-dot"
                title="New admin update — click to dismiss"
                role="button"
                tabIndex={0}
                onClick={() => clearOffering(serviceId)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') clearOffering(serviceId); }}
              />
            )}
          </h1>
          {getStatusBadge(service.status)}
        </div>
        <div className="partner-service-detail-header-actions">
          <button className="partner-table-action-btn" title={t('common.edit')} onClick={() => setShowEdit(true)}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14.7 6.3a1 1 0 0 0 0-1.4l-1.6-1.6a1 1 0 0 0-1.4 0l-10 10V17h3.7l10-10z" />
            </svg>
          </button>
          <button className="partner-table-action-btn delete" title={t('common.remove')} onClick={() => setShowDelete(true)}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 17 6" />
              <path d="M16 6v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
            </svg>
          </button>
        </div>
      </div>

      {/* Info Grid */}
      <div className="partner-service-info-grid">
        <div className="partner-service-info-item">
          <label>{t('services.detail.serviceName')}</label>
          <p>{serviceName}</p>
        </div>
        <div className="partner-service-info-item">
          <label>{t('services.detail.category')}</label>
          <p>{categoryName || service.category.name}</p>
        </div>
        <div className="partner-service-info-item">
          <label>{t('services.detail.subcategory')}</label>
          <p>{subcategoryName}</p>
        </div>
        <div className="partner-service-info-item">
          <label>{t('services.detail.description')}</label>
          <p>{service.description || '-'}</p>
        </div>
        <div className="partner-service-info-item">
          <label>{t('services.detail.pricing')}</label>
          <p>AED {service.price.toFixed(2)}</p>
        </div>
        <div className="partner-service-info-item">
          <label>{t('services.detail.duration')}</label>
          <p>{formatDuration(service.durationMinutes)}</p>
        </div>
      </div>

      {/* Images */}
      <div className="partner-service-images-section">
        <h3>{t('services.detail.images')}</h3>
        <div className="partner-service-images-grid">
          {thumbnails.length > 0 ? (
            thumbnails.map((media) => (
              <div key={media.id} className="partner-service-image-item">
                <img src={media.downloadUrl} alt={media.originalFilename} />
                <p>{t('services.detail.thumbnail')}</p>
              </div>
            ))
          ) : (
            <div className="partner-service-image-item">
              <div style={{ width: 120, height: 90, background: '#f3f4f6', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: '0.75rem' }}>
                {t('services.detail.noThumbnail')}
              </div>
              <p>{t('services.detail.thumbnail')}</p>
            </div>
          )}

          {photos.map((media) => (
            <div key={media.id} className="partner-service-image-item">
              <img src={media.downloadUrl} alt={media.originalFilename} />
              <p>{t('services.detail.photo')}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Modals */}
      {showEdit && auth.token && (
        <AddServiceModalV2
          token={auth.token}
          editOfferingId={service.id}
          onClose={() => setShowEdit(false)}
          onSuccess={() => { fetchService(); setShowEdit(false); }}
        />
      )}

      {showDelete && auth.token && (
        <DeleteServiceModal
          token={auth.token}
          serviceId={service.id}
          serviceName={serviceName}
          onClose={() => setShowDelete(false)}
          onSuccess={() => navigate('/services')}
        />
      )}
    </div>
  );
}

export default ServiceDetailPage;
